import { Capacitor } from "@capacitor/core";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  LinearProgress,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "common/locales";
import { AnalyticsEvents, analytics } from "../common/analytics";

import LoadingScreen from "common/components/LoadingScreen";
import { getRedirectUrlWithToken } from "common/utils";
import {
  markDeviceAsRegistered,
  setupPushNotificationsAndRegisterDevice,
  shouldRegisterDevice,
} from "common/utils/deviceRegistration";
import { restoreToken } from "common/utils/token";
import {
  fetchAnimalGroupsById,
  fetchFarmById,
  fetchFieldsByIdWithResult,
  getNavigatorGeolocation,
  initializeApp,
  initializeFarm,
} from "services";
import { login as authLogin, setLoginFailed } from "services/auth";
import { useAppStore } from "stores";
import { getDefaultRoute } from "./routes";

import RuumiLogo from "assets/images/ruumi-logo-light.png";
import LoginCarousel from "./LoginCarousel";

interface LocationType {
  from: { pathname: string };
}

const DARK_GREEN = "#1C4321";
const LIGHT_GREEN = "#B5E48C";
const LINK_COLOR = "#F7FCF1";

const PageContainer = styled(Box)({
  display: "flex",
  minHeight: "100vh",
  width: "100%",
});

const CarouselSection = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("sm")]: {
    display: "block",
    flex: "0 0 60%",
    height: "100vh",
    position: "relative",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: DARK_GREEN,
  padding: theme.spacing(4),
  minHeight: "100vh",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    paddingTop: `calc(env(safe-area-inset-top) + ${theme.spacing(4)})`,
    paddingBottom: `calc(env(safe-area-inset-bottom) + ${theme.spacing(4)})`,
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "360px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const Logo = styled("img")({
  width: "120px",
  height: "auto",
  marginBottom: "32px",
});

const StyledTextField = styled(TextField)({
  width: "100%",
  marginBottom: "16px",

  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "12px",

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: LIGHT_GREEN,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: LIGHT_GREEN,
      borderWidth: "2px",
    },
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "transparent",
  },

  "& .MuiInputLabel-root": {
    color: "#666",

    "&.Mui-focused": {
      color: DARK_GREEN,
    },

    "&.MuiInputLabel-shrink": {
      backgroundColor: "white",
      padding: "0 6px",
      marginLeft: "-4px",
      borderRadius: "12px 12px 0 0",
      border: `2px solid ${LIGHT_GREEN}`,
      borderBottom: "2px solid white",
      transform: "translate(14px, -18px) scale(0.75)",
    },
  },

  "& .MuiOutlinedInput-input": {
    padding: "16px",
  },
});

const LoginButton = styled(Button)({
  width: "100%",
  backgroundColor: LIGHT_GREEN,
  color: DARK_GREEN,
  borderRadius: "12px",
  padding: "14px 24px",
  fontSize: "1.1rem",
  fontWeight: 600,
  textTransform: "none",
  marginTop: "8px",
  border: `2px solid ${LIGHT_GREEN}`,
  transition: "all 0.2s ease-in-out",

  "&:hover": {
    backgroundColor: DARK_GREEN,
    color: LIGHT_GREEN,
    borderColor: LIGHT_GREEN,
  },

  "&:disabled": {
    backgroundColor: "rgba(181, 228, 140, 0.5)",
    color: "rgba(28, 67, 33, 0.5)",
    borderColor: "transparent",
  },

  "& .MuiButton-endIcon": {
    marginLeft: "8px",
    color: "inherit",
  },

  "& .MuiSvgIcon-root": {
    color: "inherit",
  },
});

const LinksContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  marginTop: "24px",
});

const LinkButton = styled(Button)({
  color: LINK_COLOR,
  textTransform: "none",
  fontWeight: 400,
  padding: "8px 0",

  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
});

const ProgressBar = styled(LinearProgress)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,

  "& .MuiLinearProgress-bar": {
    backgroundColor: LIGHT_GREEN,
  },

  "&.MuiLinearProgress-root": {
    backgroundColor: "rgba(181, 228, 140, 0.3)",
  },
});

export default function LoginDialog() {
  // Pre-fill the email if the invite landing page (or any other flow) seeded
  // it in localStorage. Saves the farmer from re-typing the address that's
  // already known to us.
  const [mail, setMail] = useState(
    () => window.localStorage.getItem("signupEmail") ?? ""
  );
  const [password, setPassword] = useState("");
  const [isInitializing, setIsInitializing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // Use Zustand for auth state
  const authError = useAppStore((state) => state.authError);
  const loginInProgress = useAppStore((state) => state.isLoggingIn);
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  let from = { pathname: "/" };
  if (location.state) {
    from = { pathname: (location.state as LocationType).from.pathname };
  } else {
    const params = new URLSearchParams(location.search);
    if (params.has("from")) {
      from = { pathname: params.get("from") as string };
    }
  }

  const { t } = useTranslation();

  // Redirect to home if already authenticated. A pending invite token takes
  // precedence so we route back to the invite landing page (where auto-accept
  // forwards to the consent flow). Without this, the effect's replace:true
  // navigation overrides the explicit invite redirect in handleLogin.
  useEffect(() => {
    if (!isAuthenticated) return;
    const pendingInviteToken =
      window.localStorage.getItem("pendingInviteToken");
    if (pendingInviteToken) {
      navigate(`/invite/${pendingInviteToken}`, { replace: true });
      return;
    }
    navigate(from.pathname !== "/login" ? from.pathname : "/", {
      replace: true,
    });
  }, [isAuthenticated, navigate, from.pathname]);

  useEffect(() => void getNavigatorGeolocation(), []);
  restoreToken(location);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    analytics.track(AnalyticsEvents.LOGIN_STARTED);

    try {
      // Use new auth service for login (stores token in localStorage)
      console.log("Attempting login for:", mail);
      await authLogin(mail, password);

      // Show loading screen while initializing
      setIsInitializing(true);

      // Use services for app initialization
      const res = await initializeApp();
      const isCorporateUser =
        res?.user?.corporations?.length && res?.user?.corporations?.length > 0;
      const farms = res?.farms;
      const userType = isCorporateUser ? "corporate" : "farmer";

      // Setup push notifications
      if (shouldRegisterDevice()) {
        try {
          await setupPushNotificationsAndRegisterDevice();
          markDeviceAsRegistered();
        } catch (error) {
          console.error(
            "Failed to setup device registration after login:",
            error
          );
        }
      }

      // Handle special redirects
      if (from && from.pathname.includes("oauth2")) {
        navigate(from.pathname);
        return;
      }

      // Pending programme invitation: route back to the invite landing page
      // which will auto-accept and forward to the consent flow.
      const pendingInviteToken =
        window.localStorage.getItem("pendingInviteToken");
      if (pendingInviteToken) {
        navigate(`/invite/${pendingInviteToken}`);
        return;
      }

      // Handle redirect based on user type and destination
      if (farms && farms.length > 0 && !isCorporateUser) {
        try {
          if (from.pathname.includes("farm/")) {
            const farmId = from.pathname.split("/")[2];
            if (farmId) {
              const farm = await fetchFarmById(farmId);
              await initializeFarm(farm);
              navigate(from.pathname);
              return;
            }
          } else if (from.pathname.includes("field/")) {
            const fieldId = from.pathname.split("/")[2];
            const fields = await fetchFieldsByIdWithResult([fieldId]);
            if (fields.length === 1) {
              const farm = await fetchFarmById(fields[0].farm);
              await initializeFarm(farm);
              navigate(from.pathname);
              return;
            }
          } else if (from.pathname.includes("animal-group/")) {
            const animalGroupId = from.pathname.split("/")[2];
            const animalGroups = await fetchAnimalGroupsById([animalGroupId]);
            if (animalGroups[0]) {
              const farm = await fetchFarmById(animalGroups[0].farm);
              await initializeFarm(farm);
              navigate(from.pathname);
              return;
            }
          }

          // Default: initialize first farm and redirect
          const defaultFarm = farms[0];
          await initializeFarm(defaultFarm);
          if (isMobile) {
            navigate(`/farm/${defaultFarm.guid}`);
          } else {
            navigate(getDefaultRoute(userType, defaultFarm.guid));
          }
        } catch (e) {
          // Fallback to first farm on error
          const defaultFarm = farms[0];
          await initializeFarm(defaultFarm);
          if (isMobile) {
            navigate(`/farm/${defaultFarm.guid}`);
          } else {
            navigate(getDefaultRoute(userType, defaultFarm.guid));
          }
        }
      } else if (getRedirectUrlWithToken()) {
        navigate("/oauth2/redirect");
      } else if (isCorporateUser) {
        navigate(getDefaultRoute("corporate"));
      } else {
        navigate("/add-farm/enter-name");
      }

      analytics.track(AnalyticsEvents.LOGIN_COMPLETED);
      analytics.people.increment("logins");
    } catch (err) {
      console.log(err);
      setIsInitializing(false);
      setLoginFailed();
      analytics.track(AnalyticsEvents.LOGIN_FAILED);
    }
  };

  const handleReset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate(`/login/password-forgot?mail=${mail}`);
  };

  const handleSignup = (e: React.MouseEvent) => {
    e.preventDefault();
    analytics.track(AnalyticsEvents.SIGNUP_STARTED);

    if (from.pathname.includes("/oauth2/")) {
      navigate(`/signup/user-details?from=${from.pathname}`);
      return;
    }
    navigate("/signup-intro");
  };

  // Show loading screen during initialization (after successful login) or redirect
  if (isInitializing || isAuthenticated) {
    return <LoadingScreen />;
  }

  return (
    <PageContainer>
      {!isMobile && (
        <CarouselSection>
          <LoginCarousel />
        </CarouselSection>
      )}
      <FormSection>
        {loginInProgress && <ProgressBar />}
        <FormContainer>
          <Logo src={RuumiLogo} alt="Ruumi logo" />

          <form onSubmit={handleLogin} style={{ width: "100%" }}>
            <StyledTextField
              variant="outlined"
              required
              id="input-mail"
              disabled={loginInProgress}
              type="email"
              error={authError}
              value={mail}
              onChange={(e) => setMail(e.target.value)}
              label="Email"
              autoFocus={!Capacitor.isNativePlatform()}
              data-private
            />
            <StyledTextField
              variant="outlined"
              required
              id="input-password"
              disabled={loginInProgress}
              error={authError}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              label="Password"
              type="password"
            />
            <LoginButton
              type="submit"
              variant="contained"
              disabled={loginInProgress}
              endIcon={<ArrowForwardIcon />}
              disableElevation
            >
              Log In
            </LoginButton>
          </form>

          <LinksContainer>
            <LinkButton onClick={handleReset} disabled={loginInProgress}>
              Forgot Password?
            </LinkButton>
            <LinkButton onClick={handleSignup} disabled={loginInProgress}>
              Create Account
            </LinkButton>
          </LinksContainer>
        </FormContainer>
      </FormSection>
    </PageContainer>
  );
}
